import icNo1 from "assets/ic_number_1.png";
import icNo2 from "assets/ic_number_2.png";
import icNoLight1 from "assets/ic_number_light_1.png";
import icNoLight2 from "assets/ic_number_light_2.png";
import icNoLight3 from "assets/ic_number_light_3.png";
import AppHr from "components/Commons/AppHr/AppHr";
import BottomBar from "components/Commons/BottomBar/bottomBar";
import Content from "components/Commons/Content/Content";
import WholePageLoading from "components/Commons/WholePageLoading/WholePageLoading";
import useAuthQuery from "globalQueries/useAuthQuery";
import SaveitHTTApiService from "helper/apiService/SaveitHTTApiService";
import TokenStorage from "helper/apiService/TokenStorage";
import { logger } from "helper/debug/logger";
import { HTTLocalStorage } from "helper/storageHelper";
import AppURLs from "pages/AppInfo/AppUrl";
import {
  EasyAuthRequestCode,
  goToEasyAuth,
  goToProcessing,
} from "pages/Common/EasyAuthHandler";
import useGlobalLoading from "pages/Common/Hook/useGlobalLoading";
import useGlobalToast from "pages/Common/Hook/useGlobalToast";
import {
  isKtLguReferrerCode,
  validateReferrerCodeFromServer,
} from "pages/Common/Hook/useReferrer";
import useURLParams from "pages/Common/Hook/useURLParams";
import React, { useCallback, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import "../HTT.scss";
import httRoutes, { makeHTTUrl } from "../httRoute";
import useAnalytics, { GA4Event } from "pages/Common/Hook/useAnalytics";

export const HTT_PARAM_REDIRECT = "redirect_to_fetch";
const HTT_PARAM_EVENT = "event";
const imgSrcIntro =
  "https://taxasone-drive.s3.ap-northeast-2.amazonaws.com/public/htt/img_htt_4.jpg";

const HTTIntro = () => {
  const history = useHistory();
  const { referral_code } = useParams();
  const { getParam } = useURLParams();
  const { setLoading } = useGlobalLoading();
  const { showToast, NoticeToast: NoticeToastComponent } = useGlobalToast(
    "SAVEIT에서 제공하는 서비스입니다.",
    5000,
  );
  const { sendGA4Event } = useAnalytics();
  const { userInfo } = useAuthQuery(true);

  /* HTT 서비스 중지를 위해 redirect */
  useEffect(() => {
    history.push(AppURLs.root);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkReferral = useCallback(
    async (referral_code) => {
      try {
        // 레퍼러 코드가 유효한지 서버에서 확인, 유효한 코드인 경우 localStorage 에 저장
        const res = await validateReferrerCodeFromServer(referral_code);
        if (res.status === "success") {
          if (isKtLguReferrerCode(referral_code)) {
            showToast();
          }
        }
      } catch (error) {
        logger("CheckReferralCode error: ", error);
      }
    },
    [showToast],
  );

  const checkIsFetchRequired = async () => {
    // Fetching 여부 확인: new_fetch === 1이면 Fetching 필요
    const res = await SaveitHTTApiService.checkNewFetch();
    return res?.data?.new_fetch === 1 ? true : false;
  };

  useEffect(() => {
    if (referral_code) {
      checkReferral(referral_code);
    }
  }, [referral_code, checkReferral, history]);

  const eventCode = useMemo(() => {
    return getParam(HTT_PARAM_EVENT) || "";
  }, [getParam]);

  useEffect(() => {
    // 로딩시 GA4 전환 이벤트 전송
    sendGA4Event(GA4Event.PAGE_HTT_INTRO, {
      referrer_code: referral_code || "null",
      event_code: eventCode || "null",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNext = useCallback(async () => {
    if (!userInfo?.id) {
      return TokenStorage.redirectKakaoLogin(
        AppURLs.common.httIntro + `?${HTT_PARAM_REDIRECT}=true`,
      );
    }
    // Home 에서 Intro 재진입하지 않도록
    HTTLocalStorage.setIntroShown(true);
    setLoading(true);
    const isFetchRequired = await checkIsFetchRequired();
    setLoading(false);
    // 일단은 페칭완료 시간을 업데이트
    HTTLocalStorage.setHTTFetchingDone();
    // Fetching 여부 확인
    if (isFetchRequired) {
      goToEasyAuth(history, EasyAuthRequestCode.fetchHttData, {});
    } else {
      HTTLocalStorage.setHTTFetchingDone();
      if (!HTTLocalStorage.getHTTFetchingExpired()) {
        // Fetching 이후 1일 이내에는 fetching 을 다시 하지 않는다.
        const target = makeHTTUrl(httRoutes.overview.url);
        history.replace(target);
      } else {
        goToProcessing(history, EasyAuthRequestCode.fetchHttDataExtra, {});
      }
    }
  }, [history, setLoading, userInfo?.id]);

  useEffect(() => {
    if (getParam(HTT_PARAM_REDIRECT) === "true") {
      onNext();
    }
  }, [getParam, onNext]);

  if (getParam(HTT_PARAM_REDIRECT) === "true") {
    return <WholePageLoading />;
  }

  return (
    <Content
      isSubPage
      maxHeight
      subHeader="주택양도세 환급"
      useBackButton={referral_code ? false : true}
      extra={<BottomBar text="환급 확인하기" onClick={onNext} />}
    >
      <div className="flex-row-between align-items-center my-4 px-3">
        <div className="me-2">
          <div className="fw-bold fs-5 text-orange">주택양도세</div>
          <div className="fw-500 fs-5-5">
            환급 가능 여부
            <br />
            세이브잇이 딱 알려 드려요!
          </div>
        </div>
        <img src={imgSrcIntro} width={80} height={80} alt="" />
      </div>

      <div className="section-gap mb-3" />

      <div className="fw-bold fs-5-5 flex-row-center">
        <span className="text-thick-underline-full-width">주택양도세 환급</span>
      </div>

      <div className="responsive-100-70 mt-4">
        <div className="flex-row-start mt-3">
          <img src={icNo1} width={32} height={32} alt="" />
          <div className="ms-2">
            <span className="fw-bold text-vat-theme">
              주택양도세 환급 가능합니다!
            </span>
            <br />
            지난 5년동안 20여 차례 부동산 정책 변경으로 여러분의 양도세 신고에
            미처 반영되지 못한 공제 혜택이 있을 확률이 매우 높습니다.
          </div>
        </div>
        <div className="flex-row-start mt-3">
          <img src={icNo2} width={32} height={32} alt="" />
          <div className="ms-2">
            <span className="fw-bold text-vat-theme">
              환급 여부 확인 1분이면 됩니다!
            </span>
            <br />
            하단의 환급 확인하기 버튼을 누르면 환급 여부를 무료로 확인하실수
            있어요.
          </div>
        </div>
      </div>

      <AppHr className="mx-2 my-3" dashed size={2} />

      <div className="fw-bold fs-5-5 flex-row-center">
        <span className="text-thick-underline-full-width">
          환급 여부 꼭 확인하세요
        </span>
      </div>

      <div className="round-border-box responsive-100-70 mt-4 py-4 px-3">
        <div className="flex-row-start">
          <img src={icNoLight1} width={24} height={24} alt="" />
          <div className="ms-2">2018년 이후 주택양도세를 낸 적이 있다.</div>
        </div>
        <div className="flex-row-start mt-2">
          <img src={icNoLight2} width={24} height={24} alt="" />
          <div className="ms-2">2주택 이상 보유했었다.</div>
        </div>
        <div className="flex-row-start mt-2">
          <img src={icNoLight3} width={24} height={24} alt="" />
          <div className="ms-2">3년 이상 보유한 주택이었다.</div>
        </div>
      </div>

      <div className="my-3">&nbsp;</div>

      <NoticeToastComponent />
    </Content>
  );
};

export default HTTIntro;
